import { useCurrentUser } from '../account/useAuth';
import { useClassroomControl } from '../teacher/useTeacher';
import './ClassroomPauseOverlay.css';

/**
 * Full-screen overlay shown to a classroom kid when their teacher
 * has paused the studio.
 *
 * PLAN.md framing: "pause / lock the studio for the whole class
 * during discussion." Capture and Train inside the studio remain
 * physically clickable but the overlay sits above them and intercepts
 * pointer events; the kid sees a kid-friendly "everyone freeze for
 * a sec" message.
 *
 * Mounted globally from `App.tsx` (alongside `BadgeToast`) so it
 * follows the kid wherever they navigate inside the studio. Hidden
 * for accounts without a `classroom_id` (parent-linked kids, free-
 * play accounts, teachers themselves).
 */
export function ClassroomPauseOverlay(): JSX.Element | null {
  const { data: user } = useCurrentUser();
  const control = useClassroomControl(user?.classroom_id ?? null);

  if (!user?.classroom_id) return null;
  if (!control.data?.paused_at) return null;

  return (
    <div
      className="bl-pause"
      role="alertdialog"
      aria-labelledby="bl-pause-title"
      aria-describedby="bl-pause-body"
      data-testid="classroom-pause-overlay"
    >
      <div className="bl-pause__card">
        <div className="bl-pause__icon" aria-hidden="true">
          ⏸
        </div>
        <h1 id="bl-pause-title" className="bl-pause__title">
          Everyone freeze for a sec.
        </h1>
        <p id="bl-pause-body" className="bl-pause__body">
          Your teacher paused the studio for the whole class. Look up — when they unpause, you can
          pick up right where you left off.
        </p>
        <p className="bl-pause__quiet">
          Nothing you did is lost. Capture and Train will work again as soon as your teacher
          resumes.
        </p>
      </div>
    </div>
  );
}
